import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { justForSideEffect, justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import {
  Adjustment,
  InvalidStructureDetails,
  PendingAdjustment,
  AdjustmentDetails,
  StockAdjustmentInBatchState,
  ActionPendingPayloadConfirm,
  ActionPendingPayloadCancel,
  AdjustmentsFilters,
} from './StockAdjustmentInBatchTypes'

const initialFilterDay = new Date()
initialFilterDay.setDate(initialFilterDay.getDate() - 7)

export const initialState: StockAdjustmentInBatchState = {
  uploadFilePage: {},
  totalItems: 0,
  filters: {
    startDate: initialFilterDay,
    endDate: new Date(),
  },
  adjustmentDetails: {
    user: '',
    totalAdjustments: 0,
    createdAt: '',
    items: [],
  },
  pendingAdjustment: [],
}

const submitAdjustmentFile = justForSideEffectWithPayload()

const downloadAdjustmentResultFile = justForSideEffect()
const getPendingAdjustments = justForSideEffect()

const getAdjustmentDetails = justForSideEffectWithPayload<number>()

const confirmPendingAdjustments = justForSideEffectWithPayload<ActionPendingPayloadConfirm>()
const cancelPendingAdjustments = justForSideEffectWithPayload<ActionPendingPayloadCancel>()

const setAdjustment = (
  state: StockAdjustmentInBatchState,
  action: PayloadAction<Adjustment>,
): StockAdjustmentInBatchState => ({
  ...state,
  uploadFilePage: {
    adjustment: action.payload,
  },
})

const setInvalidStructure = (
  state: StockAdjustmentInBatchState,
  action: PayloadAction<InvalidStructureDetails>,
): StockAdjustmentInBatchState => ({
  ...state,
  uploadFilePage: {
    invalidStructure: action.payload,
  },
})

const resetUploadPage = (
  state: StockAdjustmentInBatchState,
): StockAdjustmentInBatchState => ({
  ...state,
  uploadFilePage: {},
})

const setPendingAdjustment = (
  state: StockAdjustmentInBatchState,
  payload: PayloadAction<PendingAdjustment[]>,
) => ({
  ...state,
  pendingAdjustment: payload.payload,
})

const setAdjustmentDetails = (
  state: StockAdjustmentInBatchState,
  action: PayloadAction<AdjustmentDetails>,
) => ({
  ...state,
  adjustmentDetails: action.payload,
})

const setFilters = (state: StockAdjustmentInBatchState, action: PayloadAction<AdjustmentsFilters>) => ({
  ...state,
  filters: action.payload,
})

const resetMovements = (state: StockAdjustmentInBatchState) => ({
  ...state,
  pendingAdjustment: [],
  filters: {
    ...state.filters,
    page: 1,
  },
})

const reset = () => initialState

const slice = createSlice({
  name: 'stockAdjustmentInBatch',
  initialState,
  reducers: {
    submitAdjustmentFile,
    setAdjustment,
    downloadAdjustmentResultFile,
    setInvalidStructure,
    resetUploadPage,
    getPendingAdjustments,
    setPendingAdjustment,
    getAdjustmentDetails,
    setAdjustmentDetails,
    confirmPendingAdjustments,
    cancelPendingAdjustments,
    setFilters,
    resetMovements,
    reset,
  },
})

export default slice.reducer

export const { actions } = slice
